import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { notification } from "antd";
import httpclient from "../../service/httpclient";
import "./streaming.scss";

const ROUTE_PREFIX = "/streaming";
const INIT_RETRY_DELAYS_MS = [250, 750, 1500];

function isPositiveNumericId(value) {
  return /^[1-9]\d*$/.test(String(value || ""));
}

function normalizeStreamingSubPath(subPath) {
  if (!subPath || subPath === "/") return "";

  const segments = subPath.replace(/^\/+|\/+$/g, "").split("/");
  const [section, id, ...rest] = segments;

  if (section === "steam" && isPositiveNumericId(id) && rest.length === 0) {
    return `/stream/${id}`;
  }

  if (section === "series") {
    if (segments.length === 1) return "/series";
    if (segments.length === 2 && isPositiveNumericId(id))
      return `/series/${id}`;
  }

  if (
    section === "stream" &&
    segments.length === 2 &&
    isPositiveNumericId(id)
  ) {
    return `/stream/${id}`;
  }

  if (
    section === "replay" &&
    segments.length === 2 &&
    isPositiveNumericId(id)
  ) {
    return `/replay/${id}`;
  }

  if (section === "schedule" && segments.length === 1) {
    return "/schedule";
  }

  return "";
}

function Streaming() {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const history = useHistory();

  // Navigation sync state
  const lastAppRoute = useRef(null);
  const platformUrlRef = useRef(null);
  const iframeLoadedRef = useRef(false);
  const iframeReadyRef = useRef(false);
  const iframeInitializedRef = useRef(false);
  const platformUserTokenRef = useRef(null);
  const initRetryTimeoutsRef = useRef([]);
  const activatingFreeRef = useRef(false);

  function getRawCurrentSubPath() {
    const fullPath = location.pathname;
    if (fullPath.startsWith(ROUTE_PREFIX)) {
      return fullPath.slice(ROUTE_PREFIX.length) || "";
    }
    return "";
  }

  // Get current sub-path (strip route prefix and normalize to known viewer routes)
  function getCurrentSubPath() {
    return normalizeStreamingSubPath(getRawCurrentSubPath());
  }

  // Build initial iframe URL based on current browser URL
  function buildInitialIframeUrl(platformUrl) {
    const rawSubPath = getRawCurrentSubPath();
    const subPath = getCurrentSubPath();
    const search = location.search || "";

    if (rawSubPath !== subPath) {
      history.replace(ROUTE_PREFIX + subPath + search);
    }

    if (subPath) {
      return platformUrl + "/viewer" + subPath + search;
    }
    return platformUrl + "/viewer/series" + search;
  }

  function clearInitRetries() {
    initRetryTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    initRetryTimeoutsRef.current = [];
  }

  function sendInitIframe() {
    const iframe = iframeRef.current;
    const platformUrl = platformUrlRef.current;
    if (
      !iframe ||
      !platformUrl ||
      (!iframeLoadedRef.current && !iframeReadyRef.current) ||
      !platformUserTokenRef.current
    ) {
      return false;
    }

    iframe.contentWindow?.postMessage(
      {
        type: "init",
        userToken: platformUserTokenRef.current,
        theme: "icdesign",
        language: "zh",
      },
      platformUrl
    );

    return true;
  }

  function tryInitIframe() {
    if (iframeInitializedRef.current) return;

    clearInitRetries();
    if (!sendInitIframe()) return;

    initRetryTimeoutsRef.current = INIT_RETRY_DELAYS_MS.map((delay) =>
      window.setTimeout(() => {
        if (!iframeInitializedRef.current) {
          sendInitIframe();
        }
      }, delay)
    );
  }

  function requireLogin() {
    const redirectUrl = window.location.pathname + window.location.search;
    window.loginRedirectUrl = redirectUrl;
    sessionStorage.setItem("loginRedirectUrl", redirectUrl);
    history.push("/user/login");
  }

  // Initialize iframe
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    const handleLoad = () => {
      const platformUrl = platformUrlRef.current;
      if (!platformUrl || !iframe.src.startsWith(platformUrl + "/")) {
        return;
      }

      iframeLoadedRef.current = true;
      iframeInitializedRef.current = false;
      setLoading(false);
      tryInitIframe();

      // If returning from a purchase, notify iframe and show success
      const purchaseReturn = localStorage.getItem("streaming_purchase_success");
      if (purchaseReturn) {
        localStorage.removeItem("streaming_purchase_success");
        iframe.contentWindow.postMessage(
          { type: "purchaseComplete", success: true },
          platformUrl
        );
        notification.success({
          message: "购买成功",
          description: "您现在可以观看课程了",
        });
      }
    };

    const handlePageShow = (event) => {
      if (!event.persisted) return;

      iframeInitializedRef.current = false;
      tryInitIframe();
    };

    iframe.addEventListener("load", handleLoad);
    window.addEventListener("pageshow", handlePageShow);

    httpclient
      .getStreamingPlatformConfig()
      .then((data) => {
        if (!data || !data.platform_url) {
          setError("加载失败，请稍后重试");
          setLoading(false);
          return;
        }

        const platformUrl = data.platform_url;
        platformUrlRef.current = platformUrl;

        const iframeUrl = buildInitialIframeUrl(platformUrl);
        iframeLoadedRef.current = false;
        iframeReadyRef.current = false;
        iframeInitializedRef.current = false;
        clearInitRetries();
        iframe.src = iframeUrl;
      })
      .catch((err) => {
        setLoading(false);
        setError("加载失败，请稍后重试");
        console.error("Failed to get streaming platform config:", err);
      });

    return () => {
      iframe.removeEventListener("load", handleLoad);
      window.removeEventListener("pageshow", handlePageShow);
      clearInitRetries();
    };
  }, []);

  useEffect(() => {
    httpclient
      .getStreamingPlatformToken()
      .then((data) => {
        if (!data || !data.token) {
          return;
        }

        platformUserTokenRef.current = data.token;
        if (!platformUrlRef.current && data.platform_url) {
          platformUrlRef.current = data.platform_url;
        }
        tryInitIframe();
      })
      .catch((err) => {
        console.error("Failed to get streaming platform token:", err);
      });
  }, []);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      const platformUrl = platformUrlRef.current;
      if (!platformUrl || event.origin !== platformUrl) return;

      const data = event.data;

      if (event.source !== iframeRef.current?.contentWindow) return;

      if (data.type === "viewer:ready") {
        iframeReadyRef.current = true;
        iframeInitializedRef.current = false;
        tryInitIframe();
        return;
      }

      if (data.type === "viewer:initialized") {
        iframeInitializedRef.current = true;
        clearInitRetries();
        return;
      }

      // Handle replay requests
      if (data.type === "open-replay") {
        window.open("/streaming/replay/" + data.replayId, "_blank");
      }

      // Handle purchase requests
      if (data.type === "purchase") {
        handlePurchase(data.itemType, data.itemId);
      }

      if (data.type === "activate") {
        handleActivate(data.itemType, data.itemId);
      }

      if (data.type === "activateFree") {
        handleActivateFree(data.itemType, data.itemId);
      }

      // Handle app navigation (URL sync)
      if (data.type === "app:navigate" && typeof data.path === "string") {
        handleAppNavigate(data);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Handle purchase: redirect to checkout using platform-owned item IDs
  function handlePurchase(itemType, itemId) {
    if (!localStorage.token) {
      requireLogin();
      return;
    }

    const checkoutType =
      itemType === "series" ? "streaming_series" : "streaming_stream";

    localStorage.setItem(
      "streaming_purchase_return_url",
      window.location.pathname + window.location.search
    );
    window.location.href = "/checkout/" + checkoutType + "/" + itemId;
  }

  function handleActivate(itemType, itemId) {
    if (!localStorage.token) {
      requireLogin();
      return;
    }

    if (
      (itemType !== "series" && itemType !== "stream") ||
      !Number.isFinite(Number(itemId)) ||
      Number(itemId) <= 0
    ) {
      notification.error({
        message: "激活失败",
        description: "直播内容信息无效，请刷新页面后重试",
      });
      return;
    }

    localStorage.setItem(
      "streaming_activation_return_url",
      window.location.pathname + window.location.search
    );
    window.location = `/streaming/activate/${itemType}/${itemId}`;
  }

  function handleActivateFree(itemType, itemId) {
    if (!localStorage.token) {
      requireLogin();
      return;
    }

    if (
      (itemType !== "series" && itemType !== "stream") ||
      !Number.isFinite(Number(itemId)) ||
      Number(itemId) <= 0
    ) {
      notification.error({
        message: "激活失败",
        description: "直播内容信息无效，请刷新页面后重试",
      });
      return;
    }

    if (activatingFreeRef.current) {
      return;
    }

    activatingFreeRef.current = true;
    httpclient
      .activateFreeStreamingContent(itemType, Number(itemId))
      .then((response) => {
        notification.success({
          message: response?.already_has_access ? "已解锁" : "激活成功",
          description: response?.target?.title
            ? `已解锁 ${response.target.title}`
            : "您现在可以观看课程了",
        });

        const platformUrl = platformUrlRef.current;
        const iframe = iframeRef.current;
        if (platformUrl && iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: "purchaseComplete",
              itemType,
              itemId: Number(itemId),
              success: true,
            },
            platformUrl
          );
          return;
        }

        window.location.reload();
      })
      .catch((err) => {
        notification.error({
          message: "激活失败",
          description: err?.errMsg || err?.message || "请稍后重试",
        });
      })
      .finally(() => {
        activatingFreeRef.current = false;
      });
  }

  // Handle navigation from iframe -> update browser URL
  function handleAppNavigate(message) {
    const rawPath = message.path.startsWith("/")
      ? message.path.slice(1)
      : message.path;
    const queryIndex = rawPath.indexOf("?");
    const nextPath = queryIndex === -1 ? rawPath : rawPath.slice(0, queryIndex);
    const nextSearch = queryIndex === -1 ? "" : rawPath.slice(queryIndex);
    const normalizedPath = normalizeStreamingSubPath(
      nextPath ? `/${nextPath}` : ""
    );

    const fullPath = ROUTE_PREFIX + normalizedPath + nextSearch;

    // Skip if already at target
    const current = window.location.pathname + window.location.search;
    if (fullPath === current) return;

    // Mark as iframe-initiated to prevent echo
    lastAppRoute.current = normalizedPath + nextSearch;

    if (message.replace) {
      history.replace(fullPath);
    } else {
      history.push(fullPath);
    }
  }

  // Watch for browser URL changes -> send to iframe
  useEffect(() => {
    const platformUrl = platformUrlRef.current;
    const iframe = iframeRef.current;
    if (!platformUrl || !iframe || !iframe.contentWindow) return;

    const rawSubPath = getRawCurrentSubPath();
    const subPath = getCurrentSubPath();
    const search = location.search || "";
    const fullSubPath = subPath + search;

    if (rawSubPath !== subPath) {
      history.replace(ROUTE_PREFIX + subPath + search);
      return;
    }

    // Check if this change was caused by iframe navigation
    if (lastAppRoute.current === fullSubPath) {
      lastAppRoute.current = null;
      return;
    }

    // Send navigation to iframe
    const normalizedPath = subPath.startsWith("/") ? subPath.slice(1) : subPath;
    iframe.contentWindow.postMessage(
      {
        type: "host:navigate",
        path: normalizedPath,
        search: search,
      },
      platformUrl
    );
  }, [location.pathname, location.search]);

  if (error) {
    return (
      <div className="streaming-page">
        <div className="streaming-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="streaming-page">
      {loading && <div className="streaming-loading">加载中...</div>}
      <iframe
        ref={iframeRef}
        className="streaming-iframe"
        allow="autoplay; fullscreen"
        title="Streaming viewer"
        aria-label="Streaming viewer"
      />
    </div>
  );
}

export default Streaming;
